import React, {useEffect, useRef, useState} from "react";
import {Box, Fade, Popover, Stack, Typography, useMediaQuery} from "@mui/material";
import {keyframes} from "@emotion/react";
import {HugeiconsIcon} from "@hugeicons/react";
import {PlayIcon} from "@hugeicons/core-free-icons";

// ── Branched Menu (rebuilt to spec; React Bits ships components by copy-paste, not as
// an npm dependency, so the trunk/branch SVG + staggered reveal are rebuilt here) ──
// Shared by the header Start button and the Start node's dropdown.
const ROW_H = 46;         // rowHeight
const TRUNK_X = 16;       // x of the vertical trunk inside the SVG gutter
const ITEM_X = 30;        // x where each branch reaches the item
const CORNER = 10;        // rounded-corner radius of a branch
const TRUNK_W = 1.6;      // trunk / branch stroke width
const LINE_COLOR = "#D5D9E4";

const drawBranch = keyframes`
  from { stroke-dashoffset: 1; }
  to   { stroke-dashoffset: 0; }
`;
const itemReveal = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const panelSlide = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// The content rendered inside the Popover: a header row, a vertical trunk and a curved
// branch to every item. `items` = [{ key, label, description?, icon }]. `accent` drives the
// active branch/icon colour, `onSelect(key)` runs the caller's handler.
export const BranchedMenu = ({items, onSelect, accent, reduced, labelId, title = "Test the scenario"}) => {
    const [active, setActive] = useState(0);
    const itemRefs = useRef([]);

    useEffect(() => {
        const raf = requestAnimationFrame(() => itemRefs.current[0]?.focus());
        return () => cancelAnimationFrame(raf);
    }, []);

    const focusItem = (i) => {
        setActive(i);
        itemRefs.current[i]?.focus();
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); focusItem((active + 1) % items.length); }
        else if (e.key === "ArrowUp") { e.preventDefault(); focusItem((active - 1 + items.length) % items.length); }
        else if (e.key === "Home") { e.preventDefault(); focusItem(0); }
        else if (e.key === "End") { e.preventDefault(); focusItem(items.length - 1); }
        else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(items[active].key); }
    };

    const lastBranchY = (items.length - 1) * ROW_H + ROW_H / 2;
    const svgW = 40;
    const svgH = items.length * ROW_H;

    return (
        <Box sx={{animation: reduced ? "none" : `${panelSlide} .2s ease both`}}>
            <Stack direction="row" alignItems="center" gap={1} sx={{px: 1, pt: 0.5, pb: 1}}>
                <Box sx={{
                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                    bgcolor: "#EEF2FF", display: "grid", placeItems: "center", color: accent,
                }}>
                    <HugeiconsIcon icon={PlayIcon} size={11} strokeWidth={2}/>
                </Box>
                <Typography id={labelId} sx={{fontSize: 12, fontWeight: 600, color: "#4A4F66"}}>
                    {title}
                </Typography>
            </Stack>

            <Box role="menu" aria-labelledby={labelId} onKeyDown={onKeyDown} sx={{position: "relative", height: svgH}}>
                <Box
                    component="svg"
                    width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}
                    sx={{position: "absolute", top: 0, left: 6, pointerEvents: "none", overflow: "visible"}}
                >
                    <path
                        d={`M ${TRUNK_X} 0 L ${TRUNK_X} ${lastBranchY}`}
                        stroke={LINE_COLOR} strokeWidth={TRUNK_W} strokeLinecap="round" fill="none"
                    />
                    {items.map((it, i) => {
                        const by = i * ROW_H + ROW_H / 2;
                        const d = `M ${TRUNK_X} ${by - CORNER} Q ${TRUNK_X} ${by} ${TRUNK_X + CORNER} ${by} L ${ITEM_X} ${by}`;
                        const on = active === i;
                        return (
                            <path
                                key={it.key}
                                d={d}
                                pathLength={1}
                                stroke={on ? accent : LINE_COLOR}
                                strokeWidth={TRUNK_W}
                                strokeLinecap="round"
                                fill="none"
                                style={{
                                    strokeDasharray: 1,
                                    strokeDashoffset: reduced ? 0 : 1,
                                    animation: reduced ? "none" : `${drawBranch} .45s ease forwards`,
                                    animationDelay: reduced ? "0s" : `${i * 0.12}s`,
                                    transition: "stroke .15s ease",
                                }}
                            />
                        );
                    })}
                </Box>

                {items.map((it, i) => {
                    const on = active === i;
                    return (
                        <Box key={it.key} sx={{height: ROW_H, display: "flex", alignItems: "center", pl: "40px", pr: 0.5}}>
                            <Box
                                ref={(el) => (itemRefs.current[i] = el)}
                                role="menuitem"
                                tabIndex={on ? 0 : -1}
                                onClick={() => onSelect(it.key)}
                                onMouseEnter={() => setActive(i)}
                                onFocus={() => setActive(i)}
                                sx={{
                                    width: "100%", minHeight: 42, borderRadius: "12px", px: 1,
                                    display: "flex", alignItems: "center", gap: 1, cursor: "pointer", outline: "none",
                                    color: on ? accent : "#5B6180",
                                    bgcolor: on ? "#F3F5FF" : "transparent",
                                    transition: "background-color .15s ease, color .15s ease",
                                    opacity: reduced ? 1 : 0,
                                    animation: reduced ? "none" : `${itemReveal} .25s ease forwards`,
                                    animationDelay: reduced ? "0s" : `${0.25 + i * 0.12}s`,
                                }}
                            >
                                <HugeiconsIcon icon={it.icon} size={17} strokeWidth={1.8} style={{flexShrink: 0}}/>
                                <Box sx={{minWidth: 0}}>
                                    <Typography sx={{fontSize: 13, fontWeight: 500, color: "#2C2B2A", lineHeight: 1.3}}>
                                        {it.label}
                                    </Typography>
                                    {it.description && (
                                        <Typography noWrap sx={{fontSize: 11.5, color: "#6B7089", lineHeight: 1.3}}>
                                            {it.description}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

// Anchored Popover wrapper: a 300px branched panel with a fade+slide, and (by default) a
// backdrop that blurs everything behind it. Click-away and Escape close it and restore
// focus to the trigger. `blur` accepts `true` (5px), a number of px for a lighter blur, or
// `false` for a transparent click-away layer — use a light value where the trigger can't be
// lifted above the backdrop (e.g. inside the transformed react-flow canvas) so its own blur
// stays subtle.
export const BranchedMenuPopover = ({
    anchorEl,
    open,
    onClose,
    items,
    onSelect,
    accent,
    labelId,
    title,
    blur = true,
    anchorOrigin = {vertical: "bottom", horizontal: "right"},
    transformOrigin = {vertical: "top", horizontal: "right"},
}) => {
    const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
    const blurPx = blur === true ? 5 : (typeof blur === "number" ? blur : 0);
    return (
        <Popover
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            disableScrollLock
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            TransitionComponent={Fade}
            transitionDuration={reduced ? 0 : 200}
            slotProps={{
                paper: {
                    sx: {
                        mt: "8px", width: 300, overflow: "hidden",
                        bgcolor: "#fff",
                        border: "1px solid #ECEEF3",
                        borderRadius: "18px",
                        boxShadow: "0 16px 40px rgba(27,30,43,0.14)",
                        p: 1,
                    },
                },
                // Popover shallow-merges this over its internal { backdrop: { invisible: true } }.
                root: {
                    slotProps: {
                        backdrop: {
                            sx: blurPx
                                ? {
                                    backdropFilter: `blur(${blurPx}px)`,
                                    WebkitBackdropFilter: `blur(${blurPx}px)`,
                                    backgroundColor: "rgba(246,247,250,0.3)",
                                }
                                : {backgroundColor: "transparent"},
                        },
                    },
                },
            }}
        >
            <BranchedMenu
                items={items}
                onSelect={onSelect}
                accent={accent}
                reduced={reduced}
                labelId={labelId}
                title={title}
            />
        </Popover>
    );
};
